import React, { useRef, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Paper,
  Fade,
  Slide,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/system';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AssistantIcon from '@mui/icons-material/Assistant';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { useOktaAuth } from '@okta/okta-react';
import _ from 'lodash';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';

import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import { conversationApi, Feedback, messageFeedback } from 'src/api';
import { XSSAllowTags } from './sanatizeAllowables';
import { useChatbotContext } from 'src/contexts/ChatbotContext';

const Message = styled(Paper)(() => ({
  padding: '10px 16px',
  marginBottom: '12px',
  position: 'relative',
  wordBreak: 'keep-all',
  wordWrap: 'break-word',
  transition: 'all 0.3s ease',
  color: '#000',
  '&.user': {
    borderRadius: '16px 16px 0 16px',
    backgroundColor: '#f5f5f5'
  },
  '&.assistant': {
    borderRadius: '0 16px 16px 16px',
    backgroundColor: '#DEF1FF'
  },
  '& a': {
    color: '#11364d',
    fontWeight: 700,
    textDecoration: 'none',
    wordBreak: 'break-all',
    '&:hover': {
      color: '#254B65',
      textDecoration: 'underline'
    }
  },
  '& p': {
    margin: 0,
    '&last-child': {
      margin: '0.5em' // hack because not sure what the MarkDown parser will return in terms of multiple <p> tags
    }
  }
}));

export const ChatbotChat = ({ showEnlarged }: { showEnlarged: boolean }) => {
  const { authState } = useOktaAuth();
  const { activeMemberId } = useContext(AuthContext) as AuthContextType;

  const {
    handleMessageChunk,
    conversation,
    currentUserMessage,
    setUserMessage,
    startSendingCurrentUserMessage,
    isTyping,
    setIsTyping,
    setFeedback
  } = useChatbotContext();
  const abortController = new AbortController();
  const messagesEndRef = useRef<HTMLInputElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = () => {
    setIsTyping(true);
    startSendingCurrentUserMessage();

    conversationApi({
      token: authState?.accessToken?.accessToken ?? '',
      memberid: activeMemberId ?? '',
      payload: conversation,
      abortSignal: abortController.signal,
      onmessage: handleMessageChunk,
      onopen: async () => {
        setIsTyping(true);
      },
      onclose: () => {
        setIsTyping(false);
      },
      onerror: () => {
        setIsTyping(false);
      }
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const [feedbackAnchorEl, setFeedbackAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const sendFeedback = (messageId: string, feedback: Feedback) => {
    messageFeedback({
      token: authState?.accessToken?.accessToken ?? '',
      memberid: activeMemberId ?? '',
      messageId: messageId,
      feedback: feedback,
      onerror: function (_err: Error | unknown): void {
        throw new Error('Function not implemented.');
      }
    }).then(() => {
      setFeedback(messageId, feedback);
      handleFeedbackClose();
    });
  };
  const handleFeedbackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setFeedbackAnchorEl(e.currentTarget);
  };
  const handleFeedbackClose = () => {
    setFeedbackAnchorEl(null);
  };

  const messagesToDisplay = conversation?.messages.filter((mesasge) => mesasge.id) ?? [];

  useEffect(() => {
    conversation?.messages.map((m) => m.content);
    scrollToBottom();
  }, [JSON.stringify(conversation?.messages.map((m) => m.content))]);

  return (
    <Box>
      <Box
        sx={{
          flex: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'stretch',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#e0e0e0',
            borderRadius: '3px'
          }
        }}
      >
        <Box
          sx={{
            flex: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'stretch',
            maxHeight: showEnlarged ? 'auto' : '430px'
          }}
        >
          <Slide
            in={true}
            timeout={1000}
            onEntered={() => {
              scrollToBottom();
            }}
          >
            <Box display={'flex'}>
              <Avatar sx={{ bgcolor: '#11364d', mr: 1 }}>
                <SmartToyIcon />
              </Avatar>
              <Message className="assistant">
                <Box
                  className={'assistant'}
                  sx={{
                    position: 'relative',
                    flexGrow: 1,
                    ml: 2
                  }}
                >
                  <Typography sx={{ mb: '8px !important' }}>
                    Hi there! I’m your CPD Virtual Assistant. How can I help? Please note that all
                    answers are AI-generated and provided for guidance only.
                  </Typography>
                </Box>
              </Message>
            </Box>
          </Slide>
          {messagesToDisplay.map((message, index) => {
            let cleanedContent = message.content;
            let isUser = message.role === 'user';

            for (let step = 1; step < 10; step++) {
              const regexPattern = new RegExp(`\\[doc${step}\\]`, 'g');
              cleanedContent = cleanedContent.replace(regexPattern, ``);
            }

            const negativeFeedbackList = [
              { feedback: Feedback.Inaccurate, title: 'Inaccurate' },
              { feedback: Feedback.Irrelevant, title: 'Irrelevant' },
              { feedback: Feedback.WrongCitation, title: 'Wrong citation' }
            ];

            return (
              <Fade in={true} key={index} timeout={500}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  {message.role !== 'user' && (
                    <Box
                      sx={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        marginRight: 1
                      }}
                    >
                      <Avatar sx={{ bgcolor: '#11364d' }}>
                        <SmartToyIcon />
                      </Avatar>
                      <Box
                        sx={{
                          position: 'relative',
                          flexGrow: 1,
                          display: 'flex'
                        }}
                      >
                        <Box
                          sx={{
                            position: 'sticky',
                            bottom: 0,
                            margin: 'auto auto 0 auto',
                            py: '20px',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <IconButton
                            size="small"
                            color={'primary'}
                            aria-label="Positive feedback"
                            onClick={(e) => {
                              sendFeedback(
                                message.id ?? '',
                                message.feedback !== Feedback.Positive
                                  ? Feedback.Positive
                                  : Feedback.Neutral
                              );
                              e.stopPropagation();
                            }}
                            sx={{ marginLeft: 'auto', minWidth: 'initial' }}
                          >
                            {message.feedback === Feedback.Positive ? (
                              <ThumbUpIcon sx={{ fontSize: 22, color: 'inherit' }} />
                            ) : (
                              <ThumbUpOutlinedIcon sx={{ fontSize: 22, color: 'inherit' }} />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            color={'primary'}
                            aria-label="Negative feedback"
                            onClick={handleFeedbackClick}
                            sx={{ marginLeft: 'auto', minWidth: 'initial' }}
                          >
                            {!!message.feedback &&
                            negativeFeedbackList.some(
                              (item) => item.feedback === message.feedback
                            ) ? (
                              <ThumbDownIcon sx={{ fontSize: 22, color: 'inherit' }} />
                            ) : (
                              <ThumbDownOutlinedIcon sx={{ fontSize: 22, color: 'inherit' }} />
                            )}
                          </IconButton>
                          <Menu
                            id="negative-feedback-menu"
                            anchorEl={feedbackAnchorEl}
                            keepMounted
                            disableScrollLock={true}
                            open={Boolean(feedbackAnchorEl)}
                            onClose={handleFeedbackClose}
                          >
                            {negativeFeedbackList.map((item) => (
                              <MenuItem
                                onClick={() =>
                                  sendFeedback(
                                    message.id ?? '',
                                    message.feedback !== item.feedback
                                      ? item.feedback
                                      : Feedback.Neutral
                                  )
                                }
                                key={item.title}
                                value={item.title}
                                sx={{
                                  fontWeight: message.feedback === item.feedback ? '700' : null
                                }}
                              >
                                {item.title}
                              </MenuItem>
                            ))}
                          </Menu>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      maxWidth: 'calc(100% - 2.25rem)',
                      marginLeft: isUser ? 'auto' : '0',
                      marginRight: isUser ? '0' : 'auto'
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Message key={index} className={isUser ? 'user' : 'assistant'}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[[rehypeRaw]]}
                          // className={styles.citationPanelContent}
                          children={DOMPurify.sanitize(cleanedContent, {
                            ALLOWED_TAGS: XSSAllowTags
                          })}
                        />
                      </Message>
                      {message.role !== 'user' && (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: 'inherit',
                            fontSize: '0.8em',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <AssistantIcon fontSize={'inherit'} sx={{ marginRight: '0.25em' }} />
                          Generated by AI
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Fade>
            );
          })}
          {isTyping && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 5 }}>
              <CircularProgress size={20} />
              <Typography variant="caption">Bot is thinking...</Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>
      <Box
        sx={{
          padding: '16px',
          borderTop: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          position: 'absolute',
          bottom: '0px',
          left: 0,
          right: 0,
          backgroundColor: 'white'
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          autoFocus={true}
          inputProps={{
            autoFocus: true,
            onKeyPress: (event) => event.key === 'Enter' && handleSend()
          }}
          value={currentUserMessage}
          onChange={(e) => setUserMessage(e.target.value, false)}
          onKeyDown={handleKeyPress}
          size="small"
          aria-label="Message input"
          multiline
          maxRows={3}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!currentUserMessage?.trim()}
          aria-label="Send message"
          sx={{
            '&:hover': {},
            '&.Mui-disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
